import { Button, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import CouponsTable from "./table.tsx";
import CouponFilters, { defaultValues } from "./filters";
import PageStyle from "./style.ts";
import DashboardContent from "@/components/app/DashboardContent";
import DashboardHeader from "@/components/app/DashboardHeader";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import { useGetCoupons } from "@/api/hooks/useCoupons";
import { ICouponListParams } from "@/api/types/coupons";
import { Order } from "@/utils/table-utils";

export default function CouponsList() {
    const createAccess = useCheckPermissionAccess(PagesAccess.COUPON, OperationAccess.CREATE);

    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<string>("updatedAt");

    const [params, setParams] = useState<ICouponListParams>({
        search: "",
        skip: 0,
        take: 10,
        filter: {},
    });

    const { data: couponsData, isFetching, isLoading } = useGetCoupons(params, `sort[${orderBy}]=${order}`);

    const totalItems = couponsData?.data?.pagination?.totalItems;
    const coupons = couponsData?.data?.items;

    const handleChange = useCallback(
        ({ page, rowsPerPage, sort }: { page?: number, rowsPerPage?: number, sort?: string }) => {
            setParams((prev) => {
                return {
                    ...prev,
                    skip: page !== undefined ? Number(page) * Number(rowsPerPage || prev.take) : prev.skip,
                    take: rowsPerPage || prev.take,
                };
            });

            // Handle sort separately like customer API
            if (sort) {
                const [field, order] = sort.split(":");
                setOrderBy(field);
                setOrder(order as Order);
            }
        },
        []
    );

    const handleFilterOnApply = useCallback((filterData: typeof defaultValues) => {
        const { search, status, amountType, appliedTo } = filterData;

        setParams((prev) => {
            return {
                ...prev,
                search: String(search) || "",
                filter: {
                    status,
                    amountType,
                    appliedTo,
                },
            };
        });
    }, []);

    return (
        <PageStyle>
            <DashboardHeader
                title="Coupons"
                titleSuffix={
                    createAccess && (
                        <Button
                            color="primary"
                            component={Link}
                            to={ROUTE_CONSTANTS.SETTINGS.COUPONS.ADD_NEW.ROOT.ABSOLUTE}
                            variant="contained"
                        >
                            Create Coupon
                        </Button>
                    )
                }
            >
                <Typography component="p">Manage your coupon codes and discounts.</Typography>
                <Typography component="p">Create, edit, and monitor coupon usage and effectiveness.</Typography>
            </DashboardHeader>

            <DashboardContent>
                <CouponFilters onApply={handleFilterOnApply} />
                <CouponsTable
                    isFetching={isFetching}
                    isLoading={isLoading}
                    list={coupons || []}
                    page={(totalItems || 0) > 0 ? Math.max(0, Number(params.skip) / Number(params.take)) : 0}
                    rowsPerPage={Number(params.take)}
                    totalItems={totalItems || 0}
                    onChange={handleChange}
                />
            </DashboardContent>
        </PageStyle>
    );
}
